import { useEffect, useState } from "react";
import {
  type CookiePrefs,
  publishCookiePrefs,
  readCookiePrefs,
} from "../utils/cookieConsent";

const Cookies = () => {
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (readCookiePrefs()) {
      setVisible(false);
      return;
    }
    const timer = window.setTimeout(() => setVisible(true), 600);
    return () => window.clearTimeout(timer);
  }, []);

  const save = (prefs: CookiePrefs) => {
    publishCookiePrefs(prefs);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 flex justify-center p-4 pointer-events-none"
      role="dialog"
      aria-label="Cookie preferences"
      aria-live="polite"
    >
      <div className="w-full max-w-3xl bg-base-100 border border-base-300 rounded-xl shadow-lg overflow-hidden pointer-events-auto">
        {managing ? (
          <div className="px-5 py-4 flex flex-col gap-4">
            <h2 className="text-sm font-semibold text-base-content">Manage cookie preferences</h2>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-base-content">Essential</p>
                <p className="text-xs text-base-content/60 mt-0.5">
                  Required for authentication and core site functionality.
                </p>
              </div>
              <span className="text-xs font-medium text-base-content/50 shrink-0 mt-0.5">Always on</span>
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-base-content">Analytics</p>
                <p className="text-xs text-base-content/60 mt-0.5">
                  Helps us understand which pages are used (Google Analytics).
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={analytics}
                onClick={() => setAnalytics((value) => !value)}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                  analytics ? "bg-neutral" : "bg-base-300"
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 rounded-full bg-base-100 shadow transition-transform ${
                    analytics ? "translate-x-[18px]" : "translate-x-[3px]"
                  }`}
                />
              </button>
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-base-content">Marketing</p>
                <p className="text-xs text-base-content/60 mt-0.5">
                  Used to measure ads and show relevant ads on other platforms (Meta pixel).
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={marketing}
                onClick={() => setMarketing((value) => !value)}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                  marketing ? "bg-neutral" : "bg-base-300"
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 rounded-full bg-base-100 shadow transition-transform ${
                    marketing ? "translate-x-[18px]" : "translate-x-[3px]"
                  }`}
                />
              </button>
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <button
                type="button"
                onClick={() => save({ essential: true, analytics: false, marketing: false })}
                className="px-4 py-2 text-xs font-semibold text-base-content/70 bg-base-200 hover:bg-base-300 rounded-lg transition-colors"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={() => save({ essential: true, analytics, marketing })}
                className="px-4 py-2 text-xs font-semibold text-white bg-neutral rounded-lg transition-colors"
              >
                Save preferences
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <p className="text-sm text-base-content/70 flex-1 leading-relaxed">
              We use cookies for analytics and advertising.{" "}
              <a
                href="https://peak.app/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-base-content font-medium underline underline-offset-2"
              >
                Privacy Policy
              </a>
            </p>
            <div className="flex gap-2 shrink-0 self-end sm:self-auto">
              <button
                type="button"
                onClick={() => save({ essential: true, analytics: false, marketing: false })}
                className="px-4 py-2 text-sm font-semibold text-base-content/70 bg-base-200 hover:bg-base-300 rounded-lg transition-colors"
              >
                Reject
              </button>
              <button
                type="button"
                onClick={() => {
                  setAnalytics(false);
                  setMarketing(false);
                  setManaging(true);
                }}
                className="px-4 py-2 text-sm font-semibold text-base-content/70 bg-base-200 hover:bg-base-300 rounded-lg transition-colors"
              >
                Manage
              </button>
              <button
                type="button"
                onClick={() => save({ essential: true, analytics: true, marketing: true })}
                className="px-4 py-2 text-sm font-semibold text-white bg-neutral rounded-lg transition-colors"
              >
                Accept
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cookies;
